/**
 * Network Diagnostics Service for Poker Reborn
 *
 * Analyzes connection failures and provides actionable user guidance.
 * Uses a multi-step diagnostic flow:
 *   1. Check navigator.onLine
 *   2. HTTP health check to server
 *   3. Port analysis (same port vs different port)
 *
 * @see tasks/active/T08_NETWORK_DIAGNOSTICS_PLAN.md
 * @see tasks/active/T08_NETWORK_DIAGNOSTICS_IMPL.md
 */

import { getServerConfig } from '@/config/network';
import type { ConnectionDiagnostic } from '@/types/network';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Diagnostic timeout for HTTP health check (ms) */
const HEALTH_CHECK_TIMEOUT = 5000;

// =============================================================================
// DIAGNOSTIC FUNCTIONS
// =============================================================================

/**
 * Diagnose the cause of a connection failure
 *
 * Flow:
 * 1. Check navigator.onLine → NO_NETWORK
 * 2. Check for auth error in original error → AUTH_FAILED
 * 3. HTTP health check to server
 *    - HTTP works, WS failed → WS_PROTOCOL_BLOCKED
 *    - HTTP fails, different port → VPN_PORT_BLOCKED
 *    - HTTP fails, same port → BACKEND_DOWN
 */
export async function diagnoseConnectionFailure(
  wsUrl: string,
  originalError?: Error
): Promise<ConnectionDiagnostic> {
  // Step 1: Check browser's online status
  if (!navigator.onLine) {
    return {
      type: 'NO_NETWORK',
      message: 'No internet connection',
      suggestion: 'Check your WiFi or mobile data',
    };
  }

  // Step 2: Check if this was an auth error
  const errorMessage = originalError?.message.toLowerCase() ?? '';
  if (
    errorMessage.includes('bad_auth') ||
    errorMessage.includes('not_auth') ||
    errorMessage.includes('authentication failed') ||
    errorMessage.includes('invalid username')
  ) {
    return {
      type: 'AUTH_FAILED',
      message: 'Invalid username or password',
      // No suggestion - cause is obvious
    };
  }

  // Step 3: Try HTTP health check
  const serverConfig = getServerConfig();
  const healthCheckUrl = `${serverConfig.httpUrl}/health`;
  let httpReachable = false;

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => { controller.abort(); }, HEALTH_CHECK_TIMEOUT);

    await fetch(healthCheckUrl, {
      signal: controller.signal,
      mode: 'no-cors', // Allow checking even without CORS headers
    });

    clearTimeout(timeout);
    httpReachable = true;
  } catch {
    httpReachable = false;
  }

  // Step 4: Analyze based on results
  const pagePort =
    window.location.port || (window.location.protocol === 'https:' ? '443' : '80');

  let wsPort: string;
  try {
    const wsUrlObj = new URL(wsUrl);
    wsPort = wsUrlObj.port || (wsUrlObj.protocol === 'wss:' ? '443' : '80');
  } catch {
    wsPort = pagePort; // Fallback if URL parsing fails
  }

  const samePort = pagePort === wsPort;

  if (httpReachable) {
    // HTTP works but WebSocket failed
    return {
      type: 'WS_PROTOCOL_BLOCKED',
      message: 'WebSocket connection blocked',
      suggestion: 'Try disabling VPN, proxy, or ad-blocker',
      details: `HTTP OK, WebSocket failed on ${wsUrl}`,
    };
  }

  if (!samePort) {
    // Different ports and HTTP failed - likely VPN/firewall
    return {
      type: 'VPN_PORT_BLOCKED',
      message: `Connection to port ${wsPort} blocked`,
      suggestion: 'If using VPN or proxy, try disabling it',
      details: `Page on port ${pagePort}, server on port ${wsPort}`,
    };
  }

  // Same port, HTTP failed - backend likely down
  return {
    type: 'BACKEND_DOWN',
    message: 'Cannot reach game server',
    suggestion: 'The server may be down. Try again later',
    details: `Health check failed: ${healthCheckUrl}`,
  };
}

/**
 * Get diagnostic for a timeout error
 */
export function diagnoseTimeout(): ConnectionDiagnostic {
  return {
    type: 'TIMEOUT',
    message: 'Connection timed out',
    suggestion: 'Check your internet and try again',
  };
}

/**
 * Get diagnostic for an unknown error
 */
export function diagnoseUnknown(error?: Error): ConnectionDiagnostic {
  const diagnostic: ConnectionDiagnostic = {
    type: 'UNKNOWN',
    message: 'Unable to connect to server',
    suggestion: 'Check your internet and try again',
  };
  if (error?.message) {
    diagnostic.details = error.message;
  }
  return diagnostic;
}
